import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import {
  faBolt,
  faBox,
  faClock,
  faHandHolding,
  faMapMarkerAlt,
  faMotorcycle,
  faPallet,
  faStore,
  faTruck,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import {
  ArticleDisposition,
  PackageSpeed,
  ShipmentMethod,
  ShippingConfigType,
} from './shipping-config.interface';

/**
 * Reine Regeln und Auswahl-Listen rund um die Versandkonfiguration — ohne
 * Angular-Abhängigkeiten, damit sowohl das alte Formular
 * (`_shared/shipping-config-form/`) als auch der geführte Versandziel-Dialog
 * (`ticket-page/shipping-target-dialog/`) dieselbe Quelle nutzen.
 */

/**
 * Platzhalter-/System-User (Bentomax Lager = 709, nicht vergeben = 283), die
 * nicht als Ziel-Techniker wählbar sind. Spiegelt
 * ShippingConfig::PLACEHOLDER_TECHNICIAN_IDS im Backend.
 */
export const PLACEHOLDER_TECHNICIAN_IDS = [709, 283];

export function isPlaceholderTechnician(userId: number | null | undefined): boolean {
  return userId != null && PLACEHOLDER_TECHNICIAN_IDS.includes(userId);
}

export interface ShippingTypeOption {
  value: ShippingConfigType;
  label: string;
  icon: IconDefinition;
  /** Erklärtext für die Kachel-Auswahl im geführten Dialog. */
  description: string;
}

export interface ShipmentMethodOption {
  value: ShipmentMethod;
  label: string;
  icon: IconDefinition;
}

export interface PackageSpeedOption {
  value: PackageSpeed;
  label: string;
  icon: IconDefinition;
}

export const SHIPPING_TYPES: ShippingTypeOption[] = [
  {
    value: 'pickpoint',
    label: 'PickPoint',
    icon: faMapMarkerAlt,
    description:
      'Das Paket geht an eine PickPoint-Station. Der Techniker holt es dort ab — dafür wird ' +
      'automatisch ein Abholauftrag erzeugt.',
  },
  {
    value: 'store_shipping',
    label: 'Filialversand',
    icon: faStore,
    description:
      'Das Paket geht an die Filiale des Tickets. Das Material wird dort entweder auf den ' +
      'Techniker umgebucht oder direkt als verbraucht gebucht.',
  },
  {
    value: 'technician_shipping',
    label: 'Technikerversand',
    icon: faTruck,
    description:
      'Das Paket geht direkt an den Techniker — wahlweise an seine Privatadresse oder an sein ' +
      'Umbuchungslager.',
  },
  {
    value: 'customer_shipping',
    label: 'Kunden-Rückversand',
    icon: faUser,
    description:
      'Das Material geht an den Kunden zurück (Rücksendeadresse). Die Artikel werden dabei als ' +
      'verbraucht gebucht, nicht auf einen Techniker umgebucht.',
  },
];

export const SHIPMENT_METHODS: ShipmentMethodOption[] = [
  { value: 'parcel', label: 'Paket', icon: faBox },
  { value: 'pallet', label: 'Palette', icon: faPallet },
  { value: 'courier', label: 'Kurier', icon: faMotorcycle },
  { value: 'pickup', label: 'Abholung', icon: faHandHolding },
];

export const PACKAGE_SPEEDS: PackageSpeedOption[] = [
  { value: 'standard', label: 'Standard', icon: faClock },
  { value: 'express', label: 'Express', icon: faBolt },
];

/**
 * PickPoint-Versand: Palette und Abholung sind nicht erlaubt — die Station
 * nimmt nur Pakete und Kurier-Sendungen entgegen.
 */
export function visibleShipmentMethodsFor(type: ShippingConfigType): ShipmentMethodOption[] {
  if (type === 'pickpoint') {
    return SHIPMENT_METHODS.filter((m) => m.value === 'parcel' || m.value === 'courier');
  }
  return SHIPMENT_METHODS;
}

/** Kontext, aus dem sich die Verfügbarkeit der Versandtypen ableitet. */
export interface ShippingTypeAvailability {
  /** Filiale am Ticket — ohne sie ist Filialversand nicht möglich. */
  ticketStoreId?: number | null;
  /** Sind dieser Config bereits Staging-Artikel zugeordnet? */
  stagingArticlesAssigned?: boolean;
}

/**
 * Grund, warum ein Versandtyp gesperrt ist — oder null, wenn er wählbar ist.
 * Wird im alten Formular als Tooltip und im geführten Dialog direkt in der
 * Kachel angezeigt.
 */
export function shippingTypeDisabledReason(
  type: ShippingConfigType,
  availability: ShippingTypeAvailability,
): string | null {
  if (type === 'store_shipping' && availability.ticketStoreId == null) {
    return 'Nur mit Ticketfiliale möglich';
  }
  if (type === 'customer_shipping' && availability.stagingArticlesAssigned) {
    return 'Kundenrückversand nicht auswählbar, solange Staging Artikel zugeordnet sind';
  }
  return null;
}

export function isShippingTypeDisabled(
  type: ShippingConfigType,
  availability: ShippingTypeAvailability,
): boolean {
  return shippingTypeDisabledReason(type, availability) !== null;
}

/**
 * Artikel-Disposition je Versandtyp. Nur beim Filialversand darf der User
 * wählen (umbuchen vs. verbraucht) — dort gewinnt seine Auswahl.
 */
export function defaultDispositionForType(
  type: ShippingConfigType,
  userChoice?: ArticleDisposition,
): ArticleDisposition {
  switch (type) {
    case 'customer_shipping':
      return 'consumed';
    case 'pickpoint':
    case 'technician_shipping':
      return 'technician_transfer';
    case 'store_shipping':
      return userChoice ?? 'technician_transfer';
  }
}

/**
 * Braucht dieser Versandtyp eine Ziel-Techniker-Auswahl? Beim Filialversand
 * nur dann, wenn das Material auf den Techniker umgebucht wird.
 */
export function showTechnicianSelectorFor(
  type: ShippingConfigType,
  disposition: ArticleDisposition,
): boolean {
  if (type === 'pickpoint' || type === 'technician_shipping') return true;
  if (type === 'store_shipping') return disposition === 'technician_transfer';
  return false;
}

export interface OffsetWording {
  mode: string;
  unit: string;
  noun: string;
  day: string;
}

/**
 * Wording der Offset-Regel je Versandtyp: PickPoint rechnet ab dem Abholtermin
 * des Abholauftrags (pickup_date), alle anderen Typen ab dem Auftragsdatum —
 * spiegelt ShippingConfig#rule_reference_date im Backend.
 */
export function offsetWordingFor(type: ShippingConfigType): OffsetWording {
  return type === 'pickpoint'
    ? {
        mode: 'X Werktage vor Abholung',
        unit: 'Werktage vor Abholtermin',
        noun: 'Abholtermin',
        day: 'Abholtag',
      }
    : {
        mode: 'X Werktage vor Auftrag',
        unit: 'Werktage vor Auftragsdatum',
        noun: 'Auftragsdatum',
        day: 'Auftragstag',
      };
}
